import React, { useMemo, useState } from 'react';

import CAutocomplete from '../../../components/CAutocomplete';
import CHorizontalScrollContainer from '../../../components/CHorizontalScrollContainer';
import CLocalizedText from '../../../components/CLocalizedText';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import CPageModule from '../../../components/CPageModule';
import CPageModulePlot from '../../../components/CPageModulePlot';
import CPandasTable from '../../../components/CPandasTable';
import CStatefulContainer from '../../../components/CStatefulContainer';
import CTab from '../../../components/CTab';
import CTabItem from '../../../components/CTabItem';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import { updateStateAttribute } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { type IResponse } from '../../../models/IResponse';
import type { ISectorPerformanceParameters } from '../../../models/ISectorPerformanceParameters';
import { type PlotlyResponse } from '../../../models/PlotlyModel';
import type { TPandasTable } from '../../../models/TPandasTable';

interface Props {
  onParamChange: (updated: Partial<ISectorPerformanceParameters>) => void;
  year: ISectorPerformanceParameters['year'];
  sectorDetailPlots: IResponse<{
    sector_plot: PlotlyResponse;
    all_plot: PlotlyResponse;
  }>;
  sectorDetailTables: IResponse<{
    monthly_returns: TPandasTable;
    monthly_performance: TPandasTable;
    annual_performance: TPandasTable;
  }>;
  sectorDistributionTable: IResponse<TPandasTable>;
  sector: ISectorPerformanceParameters['sector'];
  noOfYears: number;
}

interface Option {
  key: string;
  label: string;
}

interface State {
  activeTab: 'By Sector' | 'All Sectors';
}

export default function CSectorDetails(props: Props) {
  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);
  const [state, setState] = useState<State>({ activeTab: 'By Sector' });

  const bySector = CLocalizedTextString('sectorPerformance.sectorDetails.plotTab1');
  const allSectors = CLocalizedTextString('sectorPerformance.sectorDetails.plotTab2');
  const monthlyReturns = CLocalizedTextString('sectorPerformance.sectorDetails.table1');
  const monthlyPerformance = CLocalizedTextString('sectorPerformance.sectorDetails.table2');
  const sectorTextField = CLocalizedTextString('sectorPerformance.sectorDetails.textField.sector');
  const yearTextField = CLocalizedTextString('sectorPerformance.sectorDetails.textField.year');

  const gridColsStyle =
    dashboardOutletDimensions.w > BreakpointsInPx.BREAKPOINT_1
      ? 'repeat(2, minmax(0, 1fr))'
      : 'repeat(1, minmax(0, 1fr))';

  const sectorOptions: Option[] = useMemo(() => {
    if (!props.sectorDistributionTable?.data) return [];
    return Object.keys(props.sectorDistributionTable.data)
      .filter((name) => name !== 'All Sectors')
      .map((name) => ({ key: name, label: name }));
  }, [props.sectorDistributionTable?.data]);

  const yearOptions: Option[] = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: props.noOfYears }, (_, i) => {
      const y = currentYear - i;
      return { key: String(y), label: String(y) };
    });
  }, [props.noOfYears]);

  const sectorValue: Option[] = props.sector
    ? [{ key: props.sector, label: props.sector }]
    : sectorOptions.length > 0
      ? [sectorOptions[0]]
      : [];

  const yearValue: Option[] = props.year ? [{ key: String(props.year), label: String(props.year) }] : [];

  const YearAutocomplete =
    props.sectorDistributionTable?.data && props.sectorDetailTables?.data ? (
      <CAutocomplete
        textFieldLabel={yearTextField}
        options={yearOptions}
        value={yearValue}
        onChangeSingle={(opt) => {
          props.onParamChange({
            year: Number(opt.key)
          });
        }}
      />
    ) : null;

  const SectorAutocomplete =
    props.sectorDistributionTable?.data && props.sectorDetailTables?.data ? (
      <CAutocomplete
        textFieldLabel={sectorTextField}
        options={sectorOptions}
        value={sectorValue}
        onChangeSingle={(opt) => {
          props.onParamChange({
            sector: opt.key as ISectorPerformanceParameters['sector']
          });
        }}
      />
    ) : null;

  return (
    <CPageModule fullWidth>
      <CPageModule submodule>
        <h3>
          <CLocalizedText dictKey={'sectorPerformance.sectorDetails.title'} />
        </h3>
        <p>
          <CLocalizedText dictKey={'sectorPerformance.sectorDetails.description'} />
        </p>
      </CPageModule>
      {/* Plots */}
      <CTab
        submodule
        onChange={(label) => {
          if (label === 'By Sector') updateStateAttribute<State>({ activeTab: 'By Sector' }, setState);
          else if (label === 'All Sectors') updateStateAttribute<State>({ activeTab: 'All Sectors' }, setState);
        }}
      >
        {/* By Sector Plot Tab: Year and Sector */}
        <CTabItem label={'By Sector'} text={bySector}>
          <CPageModule fullWidth style={{ paddingTop: 0 }}>
            <CPageModule submodule>
              <div className={'grid w-full gap-2'} style={{ gridTemplateColumns: gridColsStyle }}>
                <div className={'col-span-1'}>{SectorAutocomplete}</div>
                <div className={'col-span-1'}>{YearAutocomplete}</div>
              </div>
            </CPageModule>

            <CStatefulContainer
              apiRequestState={props.sectorDetailPlots.state}
              showContent={!!props.sectorDetailPlots.data?.sector_plot}
              submodule
            >
              <CPageModulePlot
                data={props.sectorDetailPlots.data?.sector_plot?.data}
                layout={props.sectorDetailPlots.data?.sector_plot?.layout}
              />
            </CStatefulContainer>
          </CPageModule>
        </CTabItem>

        {/* All Sectors Plot Tab: Year only */}
        <CTabItem label={'All Sectors'} text={allSectors}>
          <CPageModule fullWidth style={{ paddingTop: 0 }}>
            <CPageModule submodule>
              <div className={'grid w-full gap-2'} style={{ gridTemplateColumns: gridColsStyle }}>
                <div className={'col-span-1'}>{YearAutocomplete}</div>
              </div>
            </CPageModule>

            <CStatefulContainer
              apiRequestState={props.sectorDetailPlots.state}
              showContent={!!props.sectorDetailPlots.data?.all_plot}
              submodule
            >
              <CPageModulePlot
                data={props.sectorDetailPlots.data?.all_plot?.data}
                layout={props.sectorDetailPlots.data?.all_plot?.layout}
              />
            </CStatefulContainer>
          </CPageModule>
        </CTabItem>
      </CTab>
      {/* Tables */}
      {state.activeTab === 'By Sector' && (
        <CTab submodule>
          {/* Monthly Returns Tab */}
          <CTabItem label={'Monthly Returns'} text={monthlyReturns}>
            <CPageModule fullWidth style={{ paddingTop: 0 }}>
              <CStatefulContainer
                apiRequestState={props.sectorDetailTables.state}
                showContent={!!props.sectorDetailTables.data?.monthly_returns}
                submodule
              >
                <CHorizontalScrollContainer>
                  <CPandasTable
                    data={props.sectorDetailTables.data?.monthly_returns}
                    colSpanArr={[
                      1,
                      ...Array(
                        Math.max(0, Object.keys(props.sectorDetailTables.data?.monthly_returns ?? {}).length - 1)
                      ).fill(1)
                    ]}
                    colAlignmentArr={[
                      'left',
                      ...Array(
                        Math.max(0, Object.keys(props.sectorDetailTables.data?.monthly_returns ?? {}).length - 1)
                      ).fill('right')
                    ]}
                  />
                </CHorizontalScrollContainer>
              </CStatefulContainer>
            </CPageModule>
          </CTabItem>
          {/* Monthly Performance Tab */}
          <CTabItem label={'Monthly Performance'} text={monthlyPerformance}>
            <CPageModule fullWidth style={{ paddingTop: 0 }}>
              <CStatefulContainer
                apiRequestState={props.sectorDetailTables.state}
                showContent={!!props.sectorDetailTables.data?.monthly_performance}
                submodule
              >
                <CHorizontalScrollContainer>
                  <CPandasTable
                    data={props.sectorDetailTables.data?.monthly_performance}
                    colSpanArr={[
                      1.25,
                      ...Array(
                        Math.max(0, Object.keys(props.sectorDetailTables.data?.monthly_performance ?? {}).length - 1)
                      ).fill(1)
                    ]}
                    colAlignmentArr={[
                      'left',
                      ...Array(
                        Math.max(0, Object.keys(props.sectorDetailTables.data?.monthly_performance ?? {}).length - 1)
                      ).fill('right')
                    ]}
                  />
                </CHorizontalScrollContainer>
              </CStatefulContainer>
            </CPageModule>
          </CTabItem>
        </CTab>
      )}
      {state.activeTab === 'All Sectors' && (
        <CPageModule fullWidth style={{ paddingTop: 0 }}>
          <CStatefulContainer
            apiRequestState={props.sectorDetailTables.state}
            showContent={!!props.sectorDetailTables.data?.annual_performance}
            submodule
          >
            <CHorizontalScrollContainer>
              <CPandasTable
                data={props.sectorDetailTables.data?.annual_performance}
                colSpanArr={[
                  1.25,
                  ...Array(
                    Math.max(0, Object.keys(props.sectorDetailTables.data?.annual_performance ?? {}).length - 1)
                  ).fill(1)
                ]}
                colAlignmentArr={[
                  'left',
                  ...Array(
                    Math.max(0, Object.keys(props.sectorDetailTables.data?.annual_performance ?? {}).length - 1)
                  ).fill('right')
                ]}
              />
            </CHorizontalScrollContainer>
          </CStatefulContainer>
        </CPageModule>
      )}
    </CPageModule>
  );
}
